import styles from './Event.module.scss';
import { DateLabel } from '@components/components/Timeline/components/Primitives/Primitives.js';
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import {
  Card,
  CardBody,
  Content,
  Heading,
  Link,
  useIntersectionObserver,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';
import PropTypes from 'prop-types';

const CardEventWrapper = forwardRef(({ url, target, title, ...props }, ref) =>
  url ? (
    <Link target={target} title={title} to={url} {...props} ref={ref} />
  ) : (
    <div {...props} ref={ref} />
  ),
);

CardEventWrapper.displayName = 'CardEventWrapper';

const CardEvent = ({
  event: {
    imageSize,
    organicElement,
    image,
    cardPosition,
    entryPosition,
    hasImage,
    headline,
    text,
    url: { url, title, target },
  },
  date,
  id,
  setPulse,
  isAnimated,
  containerDimension,
  isFirstItem,
  headingLevel = 2,
}) => {
  const [targetRef, isIntersecting] = useIntersectionObserver({
    threshold: 0.9,
  });

  return (
    <CardEventWrapper
      className={classNames(
        styles.eventContainer,
        styles[entryPosition],
        styles[imageSize],

        {
          [styles.isLink]: url,
          [styles[organicElement]]: organicElement && imageSize === 'large',
          [styles.contentOnly]: !hasImage,
          [styles.inContainer]:
            containerDimension && containerDimension !== 'full',
        },
      )}
      onMouseEnter={() => setPulse(prev => !prev)}
      onMouseLeave={() => setPulse(prev => !prev)}
      ref={targetRef}
      target={target}
      title={title}
      url={url}
    >
      {hasImage && image && (
        <img
          alt={image?.title || headline}
          className={classNames(
            styles.eventImage,
            styles[entryPosition],
            styles[cardPosition],
            styles[imageSize],
            {
              [styles.firstItem]: isFirstItem,
              [styles.inContainer]:
                containerDimension && containerDimension !== 'full',
            },
          )}
          src={image?.url}
        />
      )}
      <Card
        aria-labelledby={id}
        className={classNames(
          styles.eventDescription,
          styles[cardPosition],
          styles[entryPosition],
          {
            [styles[imageSize]]: hasImage && imageSize,
            [styles.contentOnly]: !hasImage,
            [styles.isAnimated]: isAnimated,
            [styles.isShown]: isIntersecting,
            [styles.inContainer]:
              containerDimension && containerDimension !== 'full',
          },
        )}
      >
        <CardBody>
          {date && (
            <DateLabel className={styles.cardDateLabel} date={date} id={id} />
          )}
          {headline && (
            <Heading as={getHeadingTag(headingLevel)} size="h6">
              {headline}
            </Heading>
          )}
          {text && (
            <Content className={styles.text} noSpacing>
              {text}
            </Content>
          )}
        </CardBody>
      </Card>
    </CardEventWrapper>
  );
};

CardEvent.propTypes = {
  containerDimension: PropTypes.string,
  date: PropTypes.string,
  event: PropTypes.shape({
    imageSize: PropTypes.string,
    organicElement: PropTypes.string,
    // exact shape of image object varies
    // eslint-disable-next-line react/forbid-prop-types
    image: PropTypes.object,
    cardPosition: PropTypes.string,
    entryPosition: PropTypes.string,
    hasImage: PropTypes.bool,
    headline: PropTypes.string,
    text: PropTypes.string,
    url: PropTypes.shape({
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
      target: PropTypes.string,
    }),
  }).isRequired,
  headingLevel: PropTypes.number,
  id: PropTypes.string.isRequired,
  isAnimated: PropTypes.bool,
  isFirstItem: PropTypes.bool,
  setPulse: PropTypes.func,
};

CardEvent.defaultProps = {
  containerDimension: null,
  date: null,
  isAnimated: false,
  isFirstItem: false,
  setPulse: () => {},
};

export default CardEvent;
